import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { assetUrl } from "../../lib/api";

/**
 * Cinematic horizontal gallery slider (springs.estate style): one large image
 * slides across at a time, with arrow controls, an index counter and a row of
 * clickable thumbnail "tabs". Clicking the main image opens the lightbox.
 */
export const BuilderGallerySlider = ({ images = [], onOpen, alt = "Home" }) => {
  const [i, setI] = useState(0);
  const n = images.length;
  if (!n) return null;
  const go = (d) => setI((p) => (p + d + n) % n);

  return (
    <div data-testid="builder-gallery-slider">
      <div className="relative rounded-2xl overflow-hidden bg-[#0C1510] aspect-[16/10] md:aspect-[16/9] group">
        <div
          className="flex h-full transition-transform duration-700"
          style={{ transform: `translateX(-${i * 100}%)`, transitionTimingFunction: "cubic-bezier(.22,1,.36,1)" }}
        >
          {images.map((g, idx) => (
            <button
              key={idx}
              onClick={() => onOpen && onOpen(assetUrl(g))}
              data-testid={`gallery-slide-${idx}`}
              className="relative w-full flex-shrink-0 h-full cursor-zoom-in"
              aria-label={`Open image ${idx + 1}`}
            >
              <img src={assetUrl(g)} alt={`${alt} — photo ${idx + 1} of ${n}`} loading={idx <= 1 ? "eager" : "lazy"} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>

        {/* gradient + zoom hint */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0C1510]/45 via-transparent to-transparent" />
        <span className="pointer-events-none absolute bottom-4 left-4 inline-flex items-center gap-2 text-[#F3EEE3]/75 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
          <Maximize2 size={13} /> Click to enlarge
        </span>

        {n > 1 && (
          <>
            <button
              onClick={() => go(-1)}
              data-testid="gallery-prev"
              aria-label="Previous"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#0C1510]/55 backdrop-blur border border-[#F3EEE3]/20 text-[#F3EEE3] flex items-center justify-center hover:bg-[#C9B79C] hover:text-[#12201A] transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => go(1)}
              data-testid="gallery-next"
              aria-label="Next"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#0C1510]/55 backdrop-blur border border-[#F3EEE3]/20 text-[#F3EEE3] flex items-center justify-center hover:bg-[#C9B79C] hover:text-[#12201A] transition-colors"
            >
              <ChevronRight size={20} />
            </button>
            <div className="absolute top-4 right-4 font-display text-sm text-[#F3EEE3] bg-[#0C1510]/55 backdrop-blur px-3 py-1 rounded-full tabular-nums">
              {String(i + 1).padStart(2, "0")} <span className="text-[#F3EEE3]/45">/ {String(n).padStart(2, "0")}</span>
            </div>
          </>
        )}
      </div>

      {n > 1 && (
        <div className="mt-4 flex gap-2.5 overflow-x-auto hide-scrollbar pb-1" data-testid="gallery-thumbs">
          {images.map((g, idx) => (
            <button
              key={idx}
              onClick={() => setI(idx)}
              data-testid={`gallery-thumb-${idx}`}
              aria-label={`Go to image ${idx + 1}`}
              className={`relative shrink-0 w-20 h-14 md:w-28 md:h-[72px] rounded-lg overflow-hidden transition-all ${
                idx === i ? "ring-2 ring-[#C9B79C] opacity-100" : "opacity-55 hover:opacity-90"
              }`}
            >
              <img src={assetUrl(g)} alt={`${alt} — thumbnail ${idx + 1}`} loading="lazy" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default BuilderGallerySlider;
